<template>
  <component
    :is="iconComponent"
    :size="sizeMap[size]"
    :stroke-width="strokeWidth"
    :class="className"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  Home,
  ShoppingBag,
  Package,
  MessageCircle,
  User,
  Search,
  ShoppingCart,
  Heart,
  Plus,
  Minus,
  X,
  Check,
  Edit,
  Trash2,
  Share2,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  ArrowRight,
  Bell,
  Mail,
  Settings,
  LogOut,
  MapPin,
  Clock,
  Calendar,
  CreditCard,
  Wallet,
  Truck,
  Plane,
  Ship,
  Globe,
  FileText,
  Download,
  Upload,
  Camera,
  Image,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  HelpCircle,
  Star,
  Filter,
  SlidersHorizontal,
  Grid,
  List,
  MoreVertical,
  Menu,
  RefreshCw,
  Copy,
  ExternalLink,
  Store,
  Building2,
  MailOpen,
  Phone,
  MapPinned,
  Send,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Activity,
  Layers,
  Database,
  Server,
  Cloud,
  CloudOff,
  Zap,
  Target,
  Crosshair,
  Shield,
  ShieldCheck,
  Key,
  Link,
  Unlink,
  Bookmark,
  Flag,
  Tag,
  Folder,
  FolderOpen,
  File,
  FilePlus,
  FileCheck,
  FileX,
  ImagePlus,
  Video,
  Play,
  Pause,
  Square,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Mic,
  Headphones,
  Smartphone,
  Monitor,
  Tablet,
  Laptop,
  Tv,
  Radio,
  Printer,
  Scan,
  QrCode,
  Barcode,
  Hash,
  AtSign,
  Quote,
  Code,
  Terminal,
  Cpu,
  HardDrive,
  Wifi,
  WifiOff,
  Bluetooth,
  Battery,
  BatteryCharging,
  Power,
  Sun,
  Moon,
  CloudSun,
  CloudRain,
  Wind,
  Thermometer,
  Droplet,
  Umbrella,
  Compass,
  Navigation,
  Map,
  Locate,
  Route,
  Move,
  Maximize,
  Minimize,
  Expand,
  Shrink,
  RotateCcw,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  ZoomIn,
  ZoomOut,
  Move3D,
  Box,
  BoxSelect,
  Layers3,
  LayoutGrid,
  LayoutList,
  Columns,
  Rows,
  SquareStack,
  RectangleHorizontal,
  RectangleVertical,
  Circle,
  Triangle,
  Octagon,
  Hexagon,
  Diamond,
  Pentagon,
  Gift,
  type LucideIcon
} from 'lucide-vue-next';

type IconName =
  | 'home' | 'shop' | 'orders' | 'messages' | 'profile' | 'search'
  | 'cart' | 'heart' | 'close' | 'check' | 'edit' | 'trash' | 'share'
  | 'chevron-left' | 'chevron-right' | 'chevron-up' | 'chevron-down'
  | 'arrow-left' | 'arrow-right' | 'bell' | 'mail' | 'settings'
  | 'logout' | 'map-pin' | 'clock' | 'calendar' | 'credit-card'
  | 'wallet' | 'truck' | 'plane' | 'ship' | 'globe' | 'file-text'
  | 'download' | 'upload' | 'camera' | 'image' | 'eye' | 'eye-off'
  | 'lock' | 'unlock' | 'alert-circle' | 'alert-triangle' | 'check-circle'
  | 'info' | 'help' | 'star' | 'filter' | 'sliders' | 'grid' | 'list'
  | 'more-vertical' | 'menu' | 'refresh' | 'copy' | 'external-link'
  | 'store' | 'building' | 'mail-open' | 'phone' | 'map-pinned' | 'send'
  | 'users' | 'dollar-sign' | 'trending-up' | 'trending-down' | 'bar-chart'
  | 'pie-chart' | 'activity' | 'layers' | 'database' | 'server' | 'cloud'
  | 'cloud-off' | 'zap' | 'target' | 'crosshair' | 'shield' | 'shield-check'
  | 'key' | 'link' | 'unlink' | 'bookmark' | 'flag' | 'tag' | 'folder'
  | 'folder-open' | 'file' | 'file-plus' | 'file-check' | 'file-x'
  | 'image-plus' | 'video' | 'play' | 'pause' | 'square' | 'skip-back'
  | 'skip-forward' | 'volume' | 'volume-x' | 'mic' | 'headphones'
  | 'smartphone' | 'monitor' | 'tablet' | 'laptop' | 'tv' | 'radio'
  | 'printer' | 'scan' | 'qr-code' | 'barcode' | 'hash' | 'at-sign'
  | 'quote' | 'code' | 'terminal' | 'cpu' | 'hard-drive' | 'wifi'
  | 'wifi-off' | 'bluetooth' | 'battery' | 'battery-charging' | 'power'
  | 'sun' | 'moon' | 'cloud-sun' | 'cloud-rain' | 'wind' | 'thermometer'
  | 'droplet' | 'umbrella' | 'compass' | 'navigation' | 'map' | 'locate'
  | 'route' | 'move' | 'maximize' | 'minimize' | 'expand' | 'shrink'
  | 'rotate-ccw' | 'rotate-cw' | 'flip-h' | 'flip-v' | 'zoom-in' | 'zoom-out'
  | 'move-3d' | 'box' | 'box-select' | 'layers-3' | 'layout-grid'
  | 'layout-list' | 'columns' | 'rows' | 'square-stack' | 'rect-h' | 'rect-v'
  | 'circle' | 'triangle' | 'octagon' | 'hexagon' | 'diamond' | 'pentagon'
  | 'plus' | 'minus' | 'package' | 'gift' | 'factory' | 'box-package'
  | 'credit-card-2' | 'wechat' | 'alipay' | 'dhl' | 'fedex';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const props = withDefaults(defineProps<{
  name: string;
  size?: IconSize;
  class?: string;
  strokeWidth?: number;
}>(), {
  size: 'md',
  class: '',
  strokeWidth: 1.5
});

const sizeMap: Record<IconSize, number> = {
  xs: 14,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  '2xl': 48
};

const iconMap: Record<IconName, LucideIcon> = {
  'home': Home,
  'shop': ShoppingBag,
  'orders': Package,
  'messages': MessageCircle,
  'profile': User,
  'search': Search,
  'cart': ShoppingCart,
  'heart': Heart,
  'close': X,
  'check': Check,
  'edit': Edit,
  'trash': Trash2,
  'share': Share2,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'bell': Bell,
  'mail': Mail,
  'settings': Settings,
  'logout': LogOut,
  'map-pin': MapPin,
  'clock': Clock,
  'calendar': Calendar,
  'credit-card': CreditCard,
  'wallet': Wallet,
  'truck': Truck,
  'plane': Plane,
  'ship': Ship,
  'globe': Globe,
  'file-text': FileText,
  'download': Download,
  'upload': Upload,
  'camera': Camera,
  'image': Image,
  'eye': Eye,
  'eye-off': EyeOff,
  'lock': Lock,
  'unlock': Unlock,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'check-circle': CheckCircle,
  'info': Info,
  'help': HelpCircle,
  'star': Star,
  'filter': Filter,
  'sliders': SlidersHorizontal,
  'grid': Grid,
  'list': List,
  'more-vertical': MoreVertical,
  'menu': Menu,
  'refresh': RefreshCw,
  'copy': Copy,
  'external-link': ExternalLink,
  'store': Store,
  'building': Building2,
  'mail-open': MailOpen,
  'phone': Phone,
  'map-pinned': MapPinned,
  'send': Send,
  'users': Users,
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'bar-chart': BarChart3,
  'pie-chart': PieChart,
  'activity': Activity,
  'layers': Layers,
  'database': Database,
  'server': Server,
  'cloud': Cloud,
  'cloud-off': CloudOff,
  'zap': Zap,
  'target': Target,
  'crosshair': Crosshair,
  'shield': Shield,
  'shield-check': ShieldCheck,
  'key': Key,
  'link': Link,
  'unlink': Unlink,
  'bookmark': Bookmark,
  'flag': Flag,
  'tag': Tag,
  'folder': Folder,
  'folder-open': FolderOpen,
  'file': File,
  'file-plus': FilePlus,
  'file-check': FileCheck,
  'file-x': FileX,
  'image-plus': ImagePlus,
  'video': Video,
  'play': Play,
  'pause': Pause,
  'square': Square,
  'skip-back': SkipBack,
  'skip-forward': SkipForward,
  'volume': Volume2,
  'volume-x': VolumeX,
  'mic': Mic,
  'headphones': Headphones,
  'smartphone': Smartphone,
  'monitor': Monitor,
  'tablet': Tablet,
  'laptop': Laptop,
  'tv': Tv,
  'radio': Radio,
  'printer': Printer,
  'scan': Scan,
  'qr-code': QrCode,
  'barcode': Barcode,
  'hash': Hash,
  'at-sign': AtSign,
  'quote': Quote,
  'code': Code,
  'terminal': Terminal,
  'cpu': Cpu,
  'hard-drive': HardDrive,
  'wifi': Wifi,
  'wifi-off': WifiOff,
  'bluetooth': Bluetooth,
  'battery': Battery,
  'battery-charging': BatteryCharging,
  'power': Power,
  'sun': Sun,
  'moon': Moon,
  'cloud-sun': CloudSun,
  'cloud-rain': CloudRain,
  'wind': Wind,
  'thermometer': Thermometer,
  'droplet': Droplet,
  'umbrella': Umbrella,
  'compass': Compass,
  'navigation': Navigation,
  'map': Map,
  'locate': Locate,
  'route': Route,
  'move': Move,
  'maximize': Maximize,
  'minimize': Minimize,
  'expand': Expand,
  'shrink': Shrink,
  'rotate-ccw': RotateCcw,
  'rotate-cw': RotateCw,
  'flip-h': FlipHorizontal,
  'flip-v': FlipVertical,
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
  'move-3d': Move3D,
  'box': Box,
  'box-select': BoxSelect,
  'layers-3': Layers3,
  'layout-grid': LayoutGrid,
  'layout-list': LayoutList,
  'columns': Columns,
  'rows': Rows,
  'square-stack': SquareStack,
  'rect-h': RectangleHorizontal,
  'rect-v': RectangleVertical,
  'circle': Circle,
  'triangle': Triangle,
  'octagon': Octagon,
  'hexagon': Hexagon,
  'diamond': Diamond,
  'pentagon': Pentagon,
  'plus': Plus,
  'minus': Minus,
  'package': Package,
  'gift': Gift,
  'factory': Building2,
  'box-package': Package,
  'credit-card-2': CreditCard,
  'wechat': MessageCircle,
  'alipay': DollarSign,
  'dhl': Truck,
  'fedex': Truck
};

// Some pages use Iconify-style "solar:*" names; map them to the Lucide set
const ALIASES: Record<string, IconName> = {
  'solar:refresh-linear': 'refresh',
  'solar:close-circle-linear': 'close',
  'solar:eye-linear': 'eye',
  'solar:eye-closed-linear': 'eye-off',
};

const iconComponent = computed(() => iconMap[ALIASES[props.name] ?? props.name as IconName] || FileText);
const className = computed(() => props.class);
</script>
